/**
 * SyncButton Component
 *
 * Button for triggering cache refresh with progress indicator.
 * Displays real-time progress from SSE streaming.
 */

import React from 'react';
import { useCacheStore } from '@shared/store/cacheStore';
import { useAuthStore } from '@shared/store/authStore';
import { Button } from '../ui';
import { RefreshCw, AlertCircle } from 'lucide-react';

interface SyncButtonProps {
  /**
   * Button variant
   * - "compact": Icon only (for toolbars)
   * - "full": Full width with text label (for mobile sidebar)
   */
  variant?: 'compact' | 'full';
  className?: string;
}

/**
 * Sync button with progress indicator
 *
 * @example
 * ```tsx
 * // Compact variant (toolbar)
 * <SyncButton />
 *
 * // Full variant (mobile sidebar)
 * <SyncButton variant="full" className="w-full justify-center" />
 * ```
 */
export function SyncButton({ variant = 'compact', className = '' }: SyncButtonProps) {
  const { refreshCache, isRefreshing, progress, message, error, clearError } = useCacheStore();
  const { isAuthenticated } = useAuthStore();

  const handleRefresh = async () => {
    try {
      clearError();
      await refreshCache();
    } catch (err) {
      console.error('[SyncButton] Refresh failed:', err);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <Button
          onClick={handleRefresh}
          disabled={isRefreshing || !isAuthenticated}
          variant="secondary"
          size="sm"
          className={className}
          data-testid='unknown-refresh-cache-btn'
          data-ux='refresh-cache'
          title={!isAuthenticated ? 'Please log in to sync cache' : 'Sync cache from Pipedrive'}>
          <RefreshCw
            className={`w-4 h-4 ${variant === 'full' ? 'mr-2' : ''} ${isRefreshing ? 'animate-spin' : ''}`}
          />
          {variant === 'full' && (isRefreshing ? 'Syncing...' : 'Sync Cache')}
        </Button>
        {isRefreshing && (
          <div className="flex items-center gap-2 min-w-[200px]">
            {/* Progress bar */}
            <div className="w-32 h-2 bg-dark-surface/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500 transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Progress text */}
            <span className="text-xs text-dark-muted whitespace-nowrap">
              {message || `${progress}%`}
            </span>
          </div>
        )}
      </div>

      {/* Error message display */}
      {error && (
        <div className="flex items-start gap-2 px-3 py-2 bg-red-900/20 border border-red-700 rounded-md text-sm text-red-400">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Sync Failed</p>
            <p className="text-xs text-red-300 mt-1">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-red-400 hover:text-red-300 text-xs"
            aria-label="Dismiss error">
            ×
          </button>
        </div>
      )}

      {/* Auth warning - Hidden on mobile, use tooltip instead */}
      {!isAuthenticated && (
        <div className="hidden md:flex items-start gap-2 px-3 py-2 bg-yellow-900/20 border border-yellow-700 rounded-md text-sm text-yellow-400">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="text-xs">Please log in to sync cache from Pipedrive</p>
        </div>
      )}
    </div>
  );
}
